import { useState, useEffect } from 'react';
import { Button, Card } from '@safetyhub/ui';
import Modal from '../Modal';
import { useAuthStore } from '../../stores/auth';
import { groupsApi, Group } from '../../lib/groups';
import { canReceiveSiteTransfer } from '../../lib/membershipAccess';
import {
  requestSiteTransfer,
  transferSiteToMyOrg,
} from '../../lib/site-transfer';
import { extractApiError } from '../../lib/api';
import { useToastStore } from '../../stores/toast';

export interface SiteTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  siteId: string;
  siteName: string;
  companyId: string;
}

type TransferMode = 'my-org' | 'other';

export default function SiteTransferModal({
  isOpen,
  onClose,
  onSuccess,
  siteId,
  siteName,
  companyId,
}: SiteTransferModalProps) {
  const { user } = useAuthStore();
  const { showToast } = useToastStore();

  const [mode, setMode] = useState<TransferMode>('other');

  // 내 조직으로 이동 상태
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  // 다른 사람에게 이관 상태
  const [recipientContact, setRecipientContact] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 현재 조직을 제외한, 현장을 이관해 갈 수 있는 내 조직 목록.
  // P6-b: 옛 `role === 'OWNER'` → 권한 **key**(`site.transfer`) — 서버 `assertCanTransfer`와 같은 축이다
  // (성질로 좁히면 서버는 허용하는데 목록에 안 뜨는 조직이 생긴다). 근거는 그 함수 doc.
  const ownerMemberships = (user?.memberships ?? []).filter(
    (m) => canReceiveSiteTransfer(m) && m.companyId !== companyId
  );

  // 선택된 조직의 그룹 목록 조회
  useEffect(() => {
    if (mode !== 'my-org' || !selectedOrgId) {
      setGroups([]);
      setSelectedGroupId('');
      return;
    }

    setIsLoadingGroups(true);
    groupsApi
      .findAll(selectedOrgId)
      .then((data) => {
        setGroups(data);
        // 그룹이 1개면 자동 선택, 아니면 기본 그룹 선택
        if (data.length === 1) {
          setSelectedGroupId(data[0].id);
        } else {
          const defaultGroup = data.find((g) => g.isDefault);
          setSelectedGroupId(defaultGroup?.id ?? data[0]?.id ?? '');
        }
      })
      .catch(() => {
        setGroups([]);
      })
      .finally(() => setIsLoadingGroups(false));
  }, [selectedOrgId, mode]);

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    setSelectedGroupId('');
  };

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      if (mode === 'my-org') {
        if (!selectedOrgId) {
          setError('이동할 조직을 선택해주세요.');
          return;
        }
        await transferSiteToMyOrg(companyId, siteId, {
          toOrganizationId: selectedOrgId,
          toGroupId: selectedGroupId || undefined,
        });
        showToast(`"${siteName}" 현장이 이동되었습니다.`, 'success');
        onSuccess();
        onClose();
      } else {
        if (!recipientContact.trim()) {
          setError('이메일 또는 휴대폰 번호를 입력해주세요.');
          return;
        }
        await requestSiteTransfer(companyId, siteId, {
          recipientContact: recipientContact.trim(),
        });
        showToast('이관 요청을 보냈습니다.', 'success');
        onSuccess();
        onClose();
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMode('other');
      setSelectedOrgId('');
      setSelectedGroupId('');
      setRecipientContact('');
      setError('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="현장 이관"
      size="md"
      preventClose={isSubmitting}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || (mode === 'my-org' && !selectedOrgId)}
          >
            {isSubmitting
              ? mode === 'my-org'
                ? '이동 중...'
                : '요청 중...'
              : mode === 'my-org'
                ? '이동'
                : '이관 요청 보내기'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* 현장명 표시 */}
        <Card surface="soft" elevation="none" padding="none" className="px-4 py-3">
          <p className="text-xs text-ink-500">현장</p>
          <p className="text-sm font-medium text-ink-900 mt-0.5">{siteName}</p>
        </Card>

        {/* 이관 방식 선택 */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-ink-700">이관 방식</p>
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="transferMode"
                value="my-org"
                checked={mode === 'my-org'}
                onChange={() => setMode('my-org')}
                className="h-4 w-4 text-brand-600 border-ink-300 focus:ring-brand-500"
                disabled={ownerMemberships.length === 0}
              />
              <span className={`text-sm ${ownerMemberships.length === 0 ? 'text-ink-400' : 'text-ink-700'}`}>
                내 다른 조직으로 이동
                {ownerMemberships.length === 0 && (
                  <span className="ml-1.5 text-xs text-ink-400">(Owner인 다른 조직 없음)</span>
                )}
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="transferMode"
                value="other"
                checked={mode === 'other'}
                onChange={() => setMode('other')}
                className="h-4 w-4 text-brand-600 border-ink-300 focus:ring-brand-500"
              />
              <span className="text-sm text-ink-700">다른 사람에게 이관</span>
            </label>
          </div>
        </div>

        {/* 내 조직으로 이동 옵션 */}
        {mode === 'my-org' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                이동할 조직
              </label>
              <select
                value={selectedOrgId}
                onChange={(e) => handleOrgChange(e.target.value)}
                className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">조직 선택...</option>
                {ownerMemberships.map((m) => (
                  <option key={m.companyId} value={m.companyId}>
                    {m.company.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedOrgId && (
              <div>
                {isLoadingGroups ? (
                  <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
                    <div className="animate-spin h-4 w-4 border-2 border-brand-500 border-t-transparent rounded-full" />
                    그룹 목록 로딩 중...
                  </div>
                ) : groups.length > 1 ? (
                  <>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      이동할 그룹
                      <span className="ml-1 text-xs font-normal text-ink-400">(선택)</span>
                    </label>
                    <select
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    >
                      <option value="">그룹 없음 (기본)</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                          {g.isDefault ? ' (기본)' : ''}
                        </option>
                      ))}
                    </select>
                  </>
                ) : null}
              </div>
            )}

            <p className="text-xs text-ink-500 bg-blue-50 rounded-card-sm px-3 py-2">
              현장과 관련된 모든 점검, 보고서가 함께 이동됩니다. 현장 배정은 초기화됩니다.
            </p>
          </div>
        )}

        {/* 다른 사람에게 이관 옵션 */}
        {mode === 'other' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                받는 사람
              </label>
              <input
                type="text"
                value={recipientContact}
                onChange={(e) => setRecipientContact(e.target.value)}
                placeholder="이메일 또는 휴대폰 번호 입력"
                className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </div>
            <p className="text-xs text-ink-500 bg-yellow-50 rounded-card-sm px-3 py-2">
              받는 사람이 이관 요청을 수락하면 현장이 이동됩니다. 요청은 7일 후 만료됩니다.
            </p>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-card-sm px-3 py-2">{error}</p>
        )}
      </div>
    </Modal>
  );
}
